import { isWebKit } from '../platform';

const REACT_ARIA_PRESSABLE_SELECTOR = '[data-react-aria-pressable]';
let disposeHandler = null;

/**
 * WebKit can miss the first pointerdown after a native drag.
 * React Aria press handling expects pointerdown before click, so replay it once when needed.
 * Related bug: https://bugs.webkit.org/show_bug.cgi?id=202287
 */
export function installWebKitPostDragPressWorkaround() {
  if (disposeHandler != null) {
    return disposeHandler;
  }

  if (isWebKit !== true || typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  let shouldPatchNextMousePress = false;
  let didReceivePointerDownAfterDrag = false;

  function clearPendingPatch() {
    shouldPatchNextMousePress = false;
    didReceivePointerDownAfterDrag = false;
  }

  function markDragCompleted() {
    shouldPatchNextMousePress = true;
    didReceivePointerDownAfterDrag = false;
  }

  function handlePointerDown() {
    if (shouldPatchNextMousePress === true) {
      didReceivePointerDownAfterDrag = true;
    }
  }

  function replayPointerDownFromMouseDown(mouseDownEvent) {
    if (typeof PointerEvent !== 'function') {
      return;
    }

    const dispatchTarget =
      mouseDownEvent.target instanceof Element
        ? mouseDownEvent.target
        : mouseDownEvent.currentTarget;

    if (!(dispatchTarget instanceof EventTarget)) {
      return;
    }

    dispatchTarget.dispatchEvent(
      new PointerEvent('pointerdown', {
        bubbles: true,
        cancelable: true,
        composed: true,
        pointerId: 1,
        pointerType: 'mouse',
        isPrimary: true,
        button: mouseDownEvent.button,
        buttons: mouseDownEvent.buttons,
        clientX: mouseDownEvent.clientX,
        clientY: mouseDownEvent.clientY,
        screenX: mouseDownEvent.screenX,
        screenY: mouseDownEvent.screenY,
        ctrlKey: mouseDownEvent.ctrlKey,
        shiftKey: mouseDownEvent.shiftKey,
        altKey: mouseDownEvent.altKey,
        metaKey: mouseDownEvent.metaKey,
      })
    );
  }

  function handleMouseDown(mouseDownEvent) {
    if (shouldPatchNextMousePress !== true || mouseDownEvent.button !== 0) {
      return;
    }

    const target = mouseDownEvent.target instanceof Element ? mouseDownEvent.target : null;
    const isReactAriaPressable = target?.closest(REACT_ARIA_PRESSABLE_SELECTOR) != null;

    if (didReceivePointerDownAfterDrag === false && isReactAriaPressable === true) {
      replayPointerDownFromMouseDown(mouseDownEvent);
    }

    clearPendingPatch();
  }

  window.addEventListener('dragend', markDragCompleted, true);
  window.addEventListener('drop', markDragCompleted, true);
  window.addEventListener('pointerdown', handlePointerDown, true);
  window.addEventListener('mousedown', handleMouseDown, true);
  window.addEventListener('blur', clearPendingPatch);

  disposeHandler = () => {
    window.removeEventListener('dragend', markDragCompleted, true);
    window.removeEventListener('drop', markDragCompleted, true);
    window.removeEventListener('pointerdown', handlePointerDown, true);
    window.removeEventListener('mousedown', handleMouseDown, true);
    window.removeEventListener('blur', clearPendingPatch);
    disposeHandler = null;
  };

  return disposeHandler;
}
